import {EHelpIndexRole, HELP, helpId, IHelpDb, IHelpFrag, IHelpIndex, IHelpIndexNode, IHelpPageTplContext, IHelpSearchResults, IHelpTopic, OHelpSearchExp} from "back/help/helpApi";
import {IEndPoint} from "lib/commons/io/io";
import {IEltInitable, OSkinableInit} from "back/commons/basis";
import {JSX} from "lib/commons/xml/dom";
import {ERROR} from "lib/core/errorReport";
import {LANG} from "lib/commons/lang";

const helpDbs = new Map();

export function getHelpLocalDb(source: IEndPoint, parent?: HelpLocalDb, prefix?: string): HelpLocalDb {
	let db = helpDbs.get(source.url);
	if (!db) {
		db = new HelpLocalDb(source, parent, prefix);
		helpDbs.set(source.url, db);
	}
	return db;
}

export class HelpLocalDb implements IHelpDb {

	source: IEndPoint;

	parent: HelpLocalDb | undefined;
	prefix: string | undefined;
	foreignFragIndexes: Map<helpId, IHelpIndex[]>;

	fragments: HelpFrag[] | undefined;

	indexes: Map<string, HelpIndex | HelpIndexNode> = new Map();


	// /** Pour résolution de fragId issus de la db parente. */
	// globalDb: IHelpDb;

	constructor(source: IEndPoint, parent?: HelpLocalDb, prefix?: string) {
		this.source = source;
		if (parent) {
			this.parent = parent;
			this.prefix = prefix + HELP.UNIONDB_SEP_ID;
			this.foreignFragIndexes = new Map();
		}
	}

	initIfNeeded(): Promise<this> | this {
		if (this.fragments === undefined) return this.fetchBase();
		return this;
	}

	protected async fetchBase(): Promise<this> {
		if (this.fragments === undefined) {
			const d = await this.source.fetchJson<JHelpDb>();
			if (d == null) {
				this.fragments = [];
				ERROR.log("HelpDb notFound" + this.source);
			} else {
				if (d.freeLibs) {
					for (const module of await Promise.all(d.freeLibs.map(p => this.source.importJs(p)))) {
						if (typeof module.initFreeLib === "function") await (module as IHelpContentFreeLib).initFreeLib(this.source);
					}
				}
				this.fragments = d.fragments.map((d, index) => new HelpFrag(d, index, this));
				for (const index of d.indexes) {
					const i = new HelpIndex(index, this);
					this.indexes.set(i.indexKey, i);
				}
			}
		}
		return this;
	}

	async getFragment(id: helpId | null): Promise<IHelpFrag | undefined> {
		return this.getFragmentSync(id);
	}

	getFragmentSync(id: helpId | null): HelpFrag | null {
		if (!id) return undefined;
		const sep = id.indexOf(HELP.UNIONDB_SEP_ID);
		if (sep >= 0) {
			if (id.startsWith(this.prefix)) return this.fragments[parseInt(id.substring(sep + 1))];
			return this.parent?.getFragmentSync(id);
		}
		return this.fragments[parseInt(id)];
	}

	addIndexToForeignFrag(index: IHelpIndex, id: helpId | null): HelpFrag | undefined {
		const frag = this.getFragmentSync(id);
		if (frag) {
			let indexes = (this.foreignFragIndexes || (this.foreignFragIndexes = new Map())).get(id);
			if (indexes == null) this.foreignFragIndexes.set(id, indexes = []);
			indexes.push(index);
		}
		return frag;
	}

	async search(crit: OHelpSearchExp): Promise<IHelpSearchResults> {
		const result: IHelpSearchResults = [];
		if (crit.text) {
			const re = new RegExp(LANG.escape4RegexpFuzzy(crit.text), "gi");
			for (const frag of this.fragments) {
				let score = 0;
				if (frag.title) score = this.searchInContent(frag.title, re, null) * 10;
				if (frag.body) score += this.searchInContent(frag.body, re, null);
				if (score > 0) {
					result.push({found: frag, score});
					if (result.length === crit.max) break;
				}
			}
		}
		result.sort((r1, r2) => r2.score - r1.score);
		return result;
	}

	async getIndex(key: string): Promise<IHelpIndex | undefined> {
		return this.indexes.get(key)
	}

	async getKeyIndexes(role: EHelpIndexRole): Promise<Set<string>> {
		const r = new Set<string>();
		for (const index of this.indexes.values()) {
			if (index.indexRole === role) r.add(index.indexKey);
		}
		return r;
	}

	async getIndexesFor(frag: IHelpFrag): Promise<(IHelpIndex | IHelpIndexNode)[]> {
		return this.getIndexesForSync(frag);
	}

	async getIndexesWithRoleFor(frag: IHelpFrag, indexRole: EHelpIndexRole): Promise<(IHelpIndex | IHelpIndexNode)[]> {
		return this.getIndexesForSync(frag).filter((i) => i.indexRole === indexRole);
	}

	protected getIndexesForSync(frag: IHelpFrag): IHelpIndex[] {
		if (!this.prefix) return (frag as HelpFrag).indexes;
		if (frag.fragId.startsWith(this.prefix)) return (frag as HelpFrag).indexes;
		return this.foreignFragIndexes.get(frag.fragId) || [];
	}

	protected searchInContent(ct: JHelpContent, re: RegExp, extractRoot: HTMLElement): number {
		let score = 0;
		if (typeof ct === "string") {
			const matches = ct.match(re);
			if (matches) {
				score = matches.length;
				if (extractRoot) {
					//TODO extract
				}
			}
		} else {
			if (ct.c) for (let i = 0, s = ct.c.length; i < s; i++) {
				score += this.searchInContent(ct.c[i], re, extractRoot);
			}
		}
		return score;
	}
}

class HelpFrag implements IHelpTopic {
	fragId: helpId;
	cls: string;
	title?: JHelpContent;
	body?: JHelpContent;
	indexes: (HelpIndex | HelpIndexNode)[] = [];
	protected _titleStr: string;

	get titleString(): string {
		if (this._titleStr === undefined) {
			if (this.title) {
				this._titleStr = extractTextContent(this.title);
			} else {
				this._titleStr = ""; //pas de titre: plutot un car qui permet de se placer en fin ?
			}
		}
		return this._titleStr;
	}

	constructor(data: JHelpFragment, index: number, helpDb: HelpLocalDb) {
		this.fragId = data.id || helpDb.prefix ? helpDb.prefix + index : index.toString();
		this.cls = data.cls || "tag";
		if (data.ti) this.title = this.resolvePaths(data.ti, helpDb.source);
		if (data.body) this.body = this.resolvePaths(data.body, helpDb.source);
	}

	isTopic(): this is IHelpTopic {return this.title != null}

	hasBody(): boolean {return this.body != null}

	makeBody(options: IHelpPageTplContext): Promise<HTMLElement> | null {
		return this.body ? Promise.resolve(this.makeElt(this.body, options)) : null;
	}

	makeTitle(options: IHelpPageTplContext): HTMLElement {
		return this.makeElt(this.title, options);
	}

	makeElt(ct: JHelpContent | null, options: IHelpPageTplContext): HTMLElement {
		if (ct == null) return <span/>;
		const r = this.makeNode(ct, options);
		return r instanceof HTMLElement ? r : <span>{r}</span>;
	}

	makeNode(ct: JHelpContent, options: IHelpPageTplContext): Node {
		if (typeof ct === "string") return document.createTextNode(ct);
		const elt = document.createElement(ct[""]);
		if (ct.a) for (const att in ct.a) {
			elt.setAttribute(att, ct.a[att]);
			if (att === "open-help") elt.onclick = function (this: HTMLElement, ev: Event) {return options.openFragId(this.getAttribute("open-help"), ev)}
		}
		if (ct.i) {
			ct.i.reg = options.reg;
			(elt as IEltInitable).initialize(ct.i);
			ct.i.reg = null;
		}
		if (ct.c) for (const ch of ct.c) elt.appendChild(this.makeNode(ch, options));
		return elt;
	}

	/** Résolution des path dans els attribut des contenus html. */
	protected resolvePaths(ct: JHelpContent, source: IEndPoint): JHelpContent {
		if (typeof ct === "object") this.resolvePathsElt(ct, source);
		return ct;
	}

	/** Résolution des path dans els attribut des contenus html. */
	protected resolvePathsElt(ct: JHelpElt, source: IEndPoint) {
		if (ct.a?.src) ct.a.src = source.resolve(ct.a.src).url;
		if (ct.c) for (let i = 0, s = ct.c.length; i < s; i++) {
			const elt = ct.c[i];
			if (typeof elt === "object") this.resolvePathsElt(elt, source);
		}
	}

	addIndex(index: HelpIndex | HelpIndexNode): this {
		this.indexes.push(index)
		return this;
	}
}

class HelpIndex implements IHelpIndex {
	indexKey: string;
	indexRole: EHelpIndexRole;
	protected members: (IHelpFrag | IHelpIndexNode)[];

	constructor(d: JHelpIndex, thisDb: HelpLocalDb) {
		this.indexKey = d.indexKey;
		this.indexRole = d.indexRole;
		this.members = d.members.map((n) => typeof n === "number" ? thisDb.fragments[n].addIndex(this) :
			typeof n === "string" ? thisDb.addIndexToForeignFrag(this, n) : new HelpIndexNode(n, this, thisDb));
	}

	async getMembers(deepFetch?: boolean): Promise<(IHelpFrag | IHelpIndexNode)[]> {return this.members}

	isTreeNode(): this is IHelpIndexNode {return false}
}

class HelpIndexNode implements IHelpIndexNode {
	frag: HelpFrag;
	_parent: HelpIndex | HelpIndexNode;

	members: (IHelpFrag | IHelpIndexNode)[];

	get parent(): IHelpIndexNode | null {return this._parent && "frag" in this._parent ? this._parent : null}

	constructor(d: JHelpIndexNode, parent: HelpIndex | HelpIndexNode, thisDb: HelpLocalDb/*, globalDb:IHelpDb résolution fragId externes. */) {
		const f = d.f;
		this.frag = typeof f === "number" ? thisDb.fragments[f].addIndex(parent) : thisDb.addIndexToForeignFrag(parent, f);
		this._parent = parent;
		this.members = d.c.map((n) =>
			typeof n === "number" ? thisDb.fragments[n].addIndex(this) :
				typeof n === "string" ? thisDb.addIndexToForeignFrag(this, n) : new HelpIndexNode(n, this, thisDb));
	}

	async getMembers(): Promise<(IHelpFrag | IHelpIndexNode)[]> {return this.members}

	isTreeNode(): this is HelpIndexNode {return true}

	get indexKey() {
		let root = this._parent;
		while (root instanceof HelpIndexNode) root = root._parent;
		return root.indexKey;
	}

	get indexRole(): EHelpIndexRole {
		let root = this._parent;
		while (root instanceof HelpIndexNode) root = root._parent;
		return root.indexRole;
	}
}

function extractTextContent(ct: JHelpContent): string {
	if (typeof ct === "string") return ct;
	let r: string[] = []
	if (ct.c) for (const ch of ct.c) {
		r.push(extractTextContent(ch));
	}
	return r.join("");
}


/**
 * Schéma JSON de la base.
 */
interface JHelpDb {
	/** Liste à plat de toutes les pages d'aide. */
	fragments: JHelpFragment[]

	/** Index des fragments. */
	indexes: JHelpIndex[]

	/** Components custom à charger. cf IHelpContentFreeLib */
	freeLibs: string[]
}

/** Fragment en json. */
interface JHelpFragment {
	/** Id stable custom (pour futur doc d'un modèle, pas utilisé dans builder) */
	id?: helpId
	/** Classe(s) du fragment */
	cls?: string
	/** Titre du fragment */
	ti?: JHelpContent
	/** Contenu du fragment */
	body?: JHelpContent
}

/** Contenu html en json. */
export type JHelpContent = JHelpElt | string;

/** Elément html en json. */
export interface JHelpElt {
	/** Obligatoire: Nom du tag. */
	"": string

	/** Attributs statiques affectés au tag. */
	a: Dict<string>

	/**
	 * Config du custom tag du framework SC.
	 * Si renseigné, this.reg est automatiquement affecté à cet objet d'init avant l'appel à IEltInitable.initialize(init).
	 */
	i?: OSkinableInit

	c?: JHelpContent[]
}

/** Index d'une Db. */
interface JHelpIndex {
	/** Nom de l'index */
	indexKey: string
	/** Role de cet index */
	indexRole: EHelpIndexRole
	/** Membres de l'index */
	members?: (number | JHelpIndexNode | helpId /* fragments étragers*/)[]
}

/** Noeud d'un index de type arbre (plan, structure xml...) */
interface JHelpIndexNode {
	f: number | helpId /* fragments étragers*/
	c?: (number | JHelpIndexNode | helpId /* fragments étragers*/)[]
}

/** Api d'un d'une freeLib. */
interface IHelpContentFreeLib {
	/** Appelé une et une seule fois. Permet de lier le code free au code de dev-core (via callerSource.resolve()). */
	initFreeLib?(callerSource: IEndPoint): Promise<void>
}